/**
 * Dynamic Time Slot Heights Hook for Week/Day Views
 * 
 * Adjusts individual time slot heights when events are expanded/collapsed.
 * Similar to useCalendarRowHeights but for time-based views.
 * 
 * Strategy:
 * 1. Let RBC render with base 140px slot height
 * 2. Detect which slots have expanded events
 * 3. Measure actual content height in those slots
 * 4. Set slot height dynamically
 * 5. Other slots remain at base height
 */

import { useLayoutEffect, useRef } from 'react';
import type { CalendarEvent, CalendarView } from '../types/calendar';

// Height constants
const BASE_SLOT_HEIGHT = 150; // Base height for collapsed events
const MIN_SLOT_HEIGHT = 150;

/**
 * Find all events within a specific time slot
 */
const findEventsInSlot = (slot: Element): HTMLElement[] => {
  // Get the parent day column
  const dayColumn = slot.closest('.rbc-day-slot');
  if (!dayColumn) return [];

  // Get all events in this day column
  const allEvents = dayColumn.querySelectorAll('.rbc-event');
  const slotRect = slot.getBoundingClientRect();
  const eventsInSlot: HTMLElement[] = [];

  allEvents.forEach((event) => {
    const eventRect = event.getBoundingClientRect();
    
    // Check if event is within this slot's vertical bounds
    const isInSlot = 
      eventRect.top >= slotRect.top - 10 && 
      eventRect.top < slotRect.bottom + 10;
    
    if (isInSlot) {
      eventsInSlot.push(event as HTMLElement);
    }
  });

  return eventsInSlot;
};

/**
 * Measure the actual height needed for a time slot based on its events
 */
const measureSlotContentHeight = (slot: Element): number => {
  const eventsInSlot = findEventsInSlot(slot);
  
  if (eventsInSlot.length === 0) {
    return BASE_SLOT_HEIGHT;
  }

  // Find the tallest event in this slot
  let maxHeight = BASE_SLOT_HEIGHT;
  
  eventsInSlot.forEach((event) => {
    const eventHeight = event.getBoundingClientRect().height;
    maxHeight = Math.max(maxHeight, eventHeight);
  });

  // Add padding
  return maxHeight + 20;
};

/**
 * Adjust time slot heights dynamically based on expanded events
 */
export const useTimeSlotDynamicHeights = (
  events: CalendarEvent[],
  expandedEventIds: string[],
  currentView: CalendarView
) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useLayoutEffect(() => {
    // Only run for week/day views
    if (currentView !== 'week' && currentView !== 'day') return;

    // Debounce: wait for DOM to settle
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    console.log(`⏰ Time slot height adjustment triggered (${expandedEventIds.length} expanded)`);

    timeoutRef.current = setTimeout(() => {
      try {
        // Find all time slot groups
        const slotGroups = document.querySelectorAll('.rbc-timeslot-group');
        
        if (slotGroups.length === 0) {
          console.warn('No time slot groups found');
          return;
        }

        console.log(`📊 Adjusting ${slotGroups.length} time slots`);

        // Wait for content to render (especially expanded content animation)
        setTimeout(() => {
          // Measure and adjust time slot heights
          slotGroups.forEach((slot, slotIndex) => {
            const eventsInSlot = findEventsInSlot(slot);
            
            if (eventsInSlot.length === 0) {
              // No events, use base height
              (slot as HTMLElement).style.setProperty('min-height', `${BASE_SLOT_HEIGHT}px`, 'important');
              return;
            }

            // Check if ANY event in this slot is expanded
            let hasExpandedEvent = false;
            let expandedEventElement: HTMLElement | null = null;
            
            eventsInSlot.forEach((event) => {
              // Check if this event has expanded content visible
              const expandedContent = event.querySelector('.expanded-content');
              console.log(`    Event in slot ${slotIndex}:`, {
                hasExpandedContent: !!expandedContent,
                eventHTML: event.innerHTML.substring(0, 100)
              });
              
              if (expandedContent) {
                hasExpandedEvent = true;
                expandedEventElement = event;
              }
            });

            if (hasExpandedEvent && expandedEventElement) {
              // First, temporarily allow the event to expand fully to measure it
              expandedEventElement.style.setProperty('overflow', 'visible', 'important');
              expandedEventElement.style.setProperty('max-height', 'none', 'important');
              expandedEventElement.style.setProperty('height', 'auto', 'important');
              
              const eventContent = expandedEventElement.querySelector('.rbc-event-content') as HTMLElement;
              if (eventContent) {
                eventContent.style.setProperty('overflow', 'visible', 'important');
                eventContent.style.setProperty('height', 'auto', 'important');
              }
              
              // Wait a moment for content to fully render
              requestAnimationFrame(() => {
                // NOW measure the actual height
                const eventHeight = expandedEventElement!.getBoundingClientRect().height;
                const expandedHeight = Math.max(eventHeight + 30, BASE_SLOT_HEIGHT);
                
                console.log(`    Measured event height: ${eventHeight}px, setting slot to: ${expandedHeight}px`);
                
                // Increase TIME SLOT height - this will naturally push content down
                const slotElement = slot as HTMLElement;
                slotElement.style.setProperty('min-height', `${expandedHeight}px`, 'important');
                
                // Set z-index so expanded content appears ABOVE
                expandedEventElement!.style.setProperty('z-index', '100', 'important');
                expandedEventElement!.style.setProperty('position', 'relative', 'important');
                
                console.log(`  ✅ Slot ${slotIndex}: 150px → ${expandedHeight}px (EXPANDED)`);
              });
            } else {
              // Reset to base height
              const slotElement = slot as HTMLElement;
              slotElement.style.setProperty('min-height', `${BASE_SLOT_HEIGHT}px`, 'important');
              
              // Keep events contained
              eventsInSlot.forEach((event) => {
                event.style.setProperty('overflow', 'hidden', 'important');
                event.style.setProperty('max-height', '145px', 'important');
                event.style.setProperty('z-index', '1', 'important');
              });
            }
          });

          console.log('✅ Time slot heights adjusted');
        }, 250); // Wait for expand animation (200ms) + buffer
      } catch (error) {
        console.error('Error adjusting time slot heights:', error);
      }
    }, 50); // Initial debounce

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [events, expandedEventIds, currentView]);
};

